// React
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Amplify
import { confirmSignUp, signIn } from "aws-amplify/auth";

// Material UI
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import { MailOutline } from "@mui/icons-material";

// Components
import { AuthFooter } from "../Components/AuthFooter";
import { OTP } from "../Components/OTP";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserEmail,
  setAuthState,
  selectAuthState,
} from "Store/AuthSlice";
import { openSnackbar } from "Store/SnackbarSlice";
import { setActiveAuthDomain } from "Auth/AuthDomain";
import { clearAppSessionStorage } from "Auth/authUtils";

// Constants
import { emeraldGreen } from "Constants/ColourConstants";
import { AUTH_DOMAINS } from "Constants/AuthConstants";

// Formik
import { useFormik } from "formik";
import * as yup from "yup";

// Styles
import { styles } from "./styles";

import { handleResendCode } from "./confirmSignUpUtils";

const ConfirmSignUp = ({ open, onClose, password }) => {
  const { t } = useTranslation();
  const userEmail = useSelector(selectUserEmail);
  const authState = useSelector(selectAuthState);
  const wasOpenRef = useRef(open);
  const dispatch = useDispatch();

  // Validation schema
  const validationSchema = yup.object({
    code: yup
      .string()
      .length(6, t("mfa.6DigitsCode"))
      .required(t("mfa.codeRequired")),
  });

  const resendCode = async () => {
    handleResendCode({ userEmail, dispatch, t });
  };

  useEffect(() => {
    const openedDialog = open && !wasOpenRef.current;

    if (openedDialog && authState !== "SignUp") {
      handleResendCode({ userEmail, dispatch, t });
    }

    wasOpenRef.current = open;
  }, [open, authState, userEmail, dispatch, t]);

  const handleConfirmSignUp = async (code) => {
    try {
      await confirmSignUp({
        username: userEmail,
        confirmationCode: code,
      });
      dispatch(
        openSnackbar({
          message:
            authState === "SignUp"
              ? t("confirmSignUp.successSignUp")
              : t("confirmSignUp.successVerify"),
          severity: "success",
        })
      );
    } catch (error) {
      const errorMessages = {
        CodeMismatchException: t("errors.invalidCode"),
        TooManyFailedAttemptsException: t("errors.tooManyAttempts"),
        UserNotFoundException: t("errors.userNotFound"),
      };

      const errorMessage = errorMessages[error.name] || error.message;

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    }

    if (authState === "ResetPassword") {
      onClose();
    } else {
      try {
        clearAppSessionStorage();
        const { isSignedIn, nextStep } = await signIn({
          username: userEmail,
          password,
        });
        if (isSignedIn && nextStep.signInStep === "DONE") {
          setActiveAuthDomain(AUTH_DOMAINS.PRIMARY);
          dispatch(setAuthState("SignedIn"));
        }
      } catch (error) {
        dispatch(openSnackbar({ message: error.message, severity: "error" }));
        onClose();
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema,
    onSubmit: (values) => {
      handleConfirmSignUp(values.code);
    },
  });

  const titleKey =
    authState === "SignUp"
      ? "confirmSignUp.confirmSignUp"
      : "confirmSignUp.verifyAccount";

  const messageKey =
    authState === "SignUp"
      ? "confirmSignUp.verifyMessage"
      : "confirmSignUp.accountExistsMessage";

  return (
    <Dialog open={open} component="form" onSubmit={formik.handleSubmit}>
      <DialogTitle color={emeraldGreen} sx={{ textTransform: "uppercase" }}>
        {t(titleKey)}
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <DialogContentText>{t(messageKey)}</DialogContentText>
        <TextField
          label={t("confirmSignUp.email")}
          variant="outlined"
          fullWidth
          value={userEmail}
          disabled
          helperText={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : t("confirmSignUp.checkSpam")
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutline />
                </InputAdornment>
              ),
            },
          }}
        />
        <OTP
          setCode={formik.setFieldValue}
          resendCode={resendCode}
          values={formik.values}
          handleChange={formik.handleChange("code")}
          errors={formik.errors}
          touched={formik.touched}
        />
        <AuthFooter showCopyright={false} />
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="contained" sx={styles.submitButton}>
          {t("confirmSignUp.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmSignUp;
